import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Send, Mail, Users, Calendar, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Onay penceresinde kullanıcının yazması gereken kelime.
const CONFIRM_WORD = 'EVET';
// "Yakın zamanda" penceresi — bu kadar gün içinde mail almış kişiler uyarıyı tetikler.
const RECENT_WINDOW_DAYS = 7;
// Bireysel seçim listesinde aynı anda çizilecek en fazla satır sayısı.
const INDIVIDUAL_LIST_CAP = 200;

const SendCampaign = () => {
  const [templates, setTemplates] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [selectionMode, setSelectionMode] = useState('all'); // 'all', 'group', 'individual', 'limit'
  const [selectedGroup, setSelectedGroup] = useState('');
  const [recipientLimit, setRecipientLimit] = useState('');
  const [excludePreviouslySent, setExcludePreviouslySent] = useState(false);
  const [previouslySentCount, setPreviouslySentCount] = useState(0);
  // Segment (etkileşime göre dinamik alıcı seçimi)
  const [segmentPresets, setSegmentPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [segmentLoading, setSegmentLoading] = useState(false);
  // Yakın zamanda mail almış alıcılar için uyarı + onay penceresi
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recentCheck, setRecentCheck] = useState(null);
  const [checkingRecent, setCheckingRecent] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  // Gönderim gerçekten yapılıyor mu (dry-run / yapılandırma durumu)
  const [emailStatus, setEmailStatus] = useState(null);
  // Alıcı toplamları/grupları — kayıtları indirmeden, sunucudan hesaplanmış.
  const [summary, setSummary] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [selectionLoading, setSelectionLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Açılışta artık TÜM alıcılar indirilmiyor. Sayfanın ihtiyacı olan
  // toplamlar/gruplar tek bir özet çağrısıyla geliyor; alıcı kayıtları
  // yalnızca "Tekil" seçim modunda, o da bir kez yükleniyor.
  const fetchData = async () => {
    setInitialLoading(true);
    try {
      const [templatesRes, summaryRes, presetsRes, statusRes] = await Promise.all([
        axios.get(`${API}/templates`),
        axios.get(`${API}/recipients/summary`),
        axios.get(`${API}/recipients/segment-presets`),
        axios.get(`${API}/email/status`)
      ]);
      setTemplates(templatesRes.data);
      setSummary(summaryRes.data);
      setSegmentPresets(presetsRes.data.presets || []);
      setEmailStatus(statusRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setInitialLoading(false);
    }
  };

  // Filtreye uyan TÜM alıcı id'lerini sunucudan alır (kayıtları indirmeden).
  const fetchIds = async (group = null) => {
    const params = { status: 'all' };
    if (group) params.group = group;
    const { data } = await axios.get(`${API}/recipients/ids`, { params });
    return data.ids || [];
  };

  // "Tekil" modu tek tek işaretleme gerektirdiği için kayıtlara ihtiyaç var.
  // Yalnızca o moda geçilince, yalnızca bir kez yüklenir.
  const ensureRecipientsLoaded = async () => {
    if (recipients.length > 0 || recipientsLoading) return;
    setRecipientsLoading(true);
    try {
      const { data } = await axios.get(`${API}/recipients`);
      setRecipients(data);
    } catch (error) {
      toast.error('Alıcı listesi yüklenemedi');
    } finally {
      setRecipientsLoading(false);
    }
  };

  const selectAllRecipients = async () => {
    setSelectionLoading(true);
    try {
      setSelectedRecipients(await fetchIds());
    } catch (error) {
      toast.error('Alıcılar seçilemedi');
    } finally {
      setSelectionLoading(false);
    }
  };

  // Varsayılan mod "Hepsine": özet gelir gelmez tüm id'ler bir kez seçilir.
  // (Önceden tüm alıcı kayıtları indirildiği için bu kendiliğinden oluyordu.)
  // Yalnızca `summary` değişince çalışmalı; bu yüzden diğer değerler bağımlılık
  // listesinde yok ve ref üzerinden okunuyor.
  const autoSelectedRef = React.useRef(false);
  useEffect(() => {
    if (!summary || summary.total === 0 || autoSelectedRef.current) return;
    autoSelectedRef.current = true;
    selectAllRecipients();
  }, [summary]);

  // Hazır segmenti çalıştırıp eşleşen alıcıları seçer.
  const applySegment = async (presetKey) => {
    const preset = segmentPresets.find((p) => p.key === presetKey);
    if (!preset) return;

    setSelectedPreset(presetKey);
    setSegmentLoading(true);
    try {
      const { data } = await axios.post(`${API}/recipients/segment`, preset.query);
      setSelectedRecipients(data.recipient_ids || []);

      if (data.count === 0) {
        toast.warning(`"${preset.label}" segmentinde alıcı bulunamadı`);
      } else {
        toast.success(`${data.count} alıcı seçildi — ${preset.label}`);
      }
    } catch (error) {
      toast.error('Segment hesaplanamadı');
      setSelectedRecipients([]);
    } finally {
      setSegmentLoading(false);
    }
  };

  // Fetch previously sent recipients when template is selected
  useEffect(() => {
    if (selectedTemplate && excludePreviouslySent) {
      fetchPreviouslySent();
    }
  }, [selectedTemplate, excludePreviouslySent]);

  const fetchPreviouslySent = async () => {
    if (!selectedTemplate) return;
    
    try {
      const response = await axios.get(`${API}/history`);
      const sentToTemplate = response.data.filter(h => h.template_id === selectedTemplate);
      const sentEmails = new Set(sentToTemplate.map(h => h.recipient_email));
      setPreviouslySentCount(sentEmails.size);
    } catch (error) {
      console.error('Error fetching previously sent:', error);
    }
  };

  const handleLimitSelection = async () => {
    const limit = parseInt(recipientLimit);
    if (isNaN(limit) || limit <= 0) {
      toast.error('Lütfen geçerli bir sayı girin');
      return;
    }

    setSelectionLoading(true);
    try {
      const ids = await fetchIds(selectionMode === 'group' ? selectedGroup : null);

      if (limit >= ids.length) {
        toast.warning(`Sadece ${ids.length} alıcı mevcut. Tümü seçildi.`);
        setSelectedRecipients(ids);
        return;
      }

      // Rastgele seçim
      const shuffled = [...ids].sort(() => 0.5 - Math.random());
      setSelectedRecipients(shuffled.slice(0, limit));
      toast.success(`${limit} alıcı rastgele seçildi`);
    } catch (error) {
      toast.error('Alıcılar seçilemedi');
    } finally {
      setSelectionLoading(false);
    }
  };

  const totalRecipients = summary?.total || 0;
  const uniqueGroups = summary?.groups || [];

  // "Tekil" modunda listelenen kayıtlar (yalnızca o modda yüklenir).
  const filteredRecipients = selectionMode === 'group' && selectedGroup
    ? recipients.filter(r => r.group === selectedGroup)
    : recipients;

  // Bireysel seçimdeki arama kutusu — sadece görünen listeyi daraltır,
  // "Tümünü Seç" hâlâ filtrelenmiş listenin tamamını seçer.
  const searchedRecipients = recipientSearch.trim()
    ? filteredRecipients.filter((r) => {
        const q = recipientSearch.trim().toLocaleLowerCase('tr-TR');
        return (
          (r.email || '').toLocaleLowerCase('tr-TR').includes(q) ||
          (r.name || '').toLocaleLowerCase('tr-TR').includes(q)
        );
      })
    : filteredRecipients;

  const visibleRecipients = searchedRecipients.slice(0, INDIVIDUAL_LIST_CAP);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRecipients(filteredRecipients.map(r => r.id));
    } else {
      setSelectedRecipients([]);
    }
  };

  // Grup seçimi artık id'leri sunucudan alıyor — grubun tamamı seçilir,
  // tarayıcıya yüklenmiş kayıtlarla sınırlı kalmaz.
  const handleGroupSelection = async (group) => {
    setSelectedGroup(group);
    setSelectionLoading(true);
    try {
      setSelectedRecipients(await fetchIds(group));
    } catch (error) {
      toast.error('Grup alıcıları seçilemedi');
    } finally {
      setSelectionLoading(false);
    }
  };

  const handleRecipientToggle = (recipientId) => {
    setSelectedRecipients(prev =>
      prev.includes(recipientId)
        ? prev.filter(id => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  // Seçili alıcıların kaçının son günlerde mail aldığını sorar.
  // Hata durumunda null döner — kontrol çökerse gönderim engellenmez.
  const fetchRecentCheck = useCallback(async () => {
    if (selectedRecipients.length === 0) return null;
    try {
      const { data } = await axios.post(`${API}/recipients/recent-send-check`, {
        recipient_ids: selectedRecipients,
        within_days: RECENT_WINDOW_DAYS,
      });
      return data;
    } catch (error) {
      console.error('Recent send check failed:', error);
      return null;
    }
  }, [selectedRecipients]);

  // Özet adımına geçildiğinde uyarıyı önden hesapla ki kullanıcı
  // "Gönder"e basmadan da durumu görsün.
  useEffect(() => {
    if (step !== 2) {
      setRecentCheck(null);
      return undefined;
    }

    let cancelled = false;
    setCheckingRecent(true);
    fetchRecentCheck()
      .then((data) => {
        if (!cancelled) setRecentCheck(data);
      })
      .finally(() => {
        if (!cancelled) setCheckingRecent(false);
      });

    return () => {
      cancelled = true;
    };
  }, [step, fetchRecentCheck]);

  const handleSend = async () => {
    if (!selectedTemplate) {
      toast.error('Lütfen bir şablon seçin');
      return;
    }
    if (selectedRecipients.length === 0) {
      toast.error('Lütfen en az bir alıcı seçin');
      return;
    }

    // Gönderim anında tekrar kontrol et — özet ekranı açıldıktan sonra
    // başka bir kampanya gitmiş olabilir.
    setCheckingRecent(true);
    const check = await fetchRecentCheck();
    setCheckingRecent(false);
    setRecentCheck(check);

    if (check && check.recent_count > 0) {
      setConfirmText('');
      setConfirmOpen(true);
      return;
    }

    await performSend();
  };

  const performSend = async () => {
    setLoading(true);
    try {
      let scheduleTimeISO = null;
      if (scheduleDate && scheduleTime) {
        const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
        scheduleTimeISO = scheduledDateTime.toISOString();
      }

      const response = await axios.post(`${API}/send-email`, {
        template_id: selectedTemplate,
        recipient_ids: selectedRecipients,
        schedule_time: scheduleTimeISO,
        exclude_previously_sent: excludePreviouslySent
      });

      if (scheduleTimeISO) {
        toast.success(`✅ Kampanya ${new Date(scheduleTimeISO).toLocaleString('tr-TR')} için zamanlandı!`);
      } else {
        toast.success(response.data.message || '✅ Kampanya gönderiliyor!');
      }
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kampanya gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  const confirmAndSend = async () => {
    if (confirmText.trim().toLocaleUpperCase('tr-TR') !== CONFIRM_WORD) return;
    setConfirmOpen(false);
    setConfirmText('');
    await performSend();
  };

  const resetForm = () => {
    setSelectedTemplate('');
    setSelectedRecipients([]);
    setScheduleDate('');
    setScheduleTime('');
    setRecentCheck(null);
    setConfirmText('');
    setStep(1);
  };

  if (step === 3) {
    return (
      <div className="flex items-center justify-center min-h-[600px]">
        <div className="text-center space-y-6 max-w-md">
          <div className="mx-auto w-24 h-24 bg-[#10b981]/10 rounded-full flex items-center justify-center">
            <CheckCircle size={48} className="text-[#10b981]" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-2">Kampanya Gönderildi!</h2>
            <p className="text-sm text-[#64748b]">
              Kampanyanız işleniyor ve emailler kısa süre içinde teslim edilecek.
            </p>
          </div>
          <div className="bg-[#f8fafc] rounded-xl p-6 border border-[#e2e8f0]">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-[#64748b] mb-1">Alıcılar</p>
                <p className="text-2xl font-bold text-[#0f172a]">{selectedRecipients.length}</p>
              </div>
              <div>
                <p className="text-sm text-[#64748b] mb-1">Durum</p>
                <p className="text-sm font-medium text-[#10b981]">İşleniyor</p>
              </div>
            </div>
          </div>
          <Button
            onClick={resetForm}
            className="w-full bg-[#6366f1] hover:bg-[#5558e3] text-white"
          >
            Başka Kampanya Gönder
          </Button>
        </div>
      </div>
    );
  }

  // Sayfa verisi gelene kadar donen gosterge — diger sayfalarla ayni bicim.
  if (initialLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4" data-testid="send-campaign-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6366f1]"></div>
        <p className="text-sm text-[#64748b]">Yükleniyor…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="send-campaign-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a] tracking-tight">Kampanya Gönder</h1>
        <p className="text-sm text-[#64748b] mt-1">Alıcılarınıza e-posta kampanyası oluşturun ve gönderin</p>
      </div>

      {/* Alıcı seçimi sunucudan hesaplanırken geri bildirim */}
      {selectionLoading && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[#eef2ff] border border-[#c7d2fe] rounded-lg">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#6366f1]"></div>
          <p className="text-sm font-medium text-[#4338ca]">Alıcılar seçiliyor…</p>
        </div>
      )}

      {/* Test modu uyarısı — kimseye gerçek mail gitmediği açıkça görünsün. */}
      {emailStatus?.dry_run && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-300 rounded-xl">
          <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Test modu açık (EMAIL_DRY_RUN)</p>
            <p className="text-sm text-amber-800 mt-0.5">
              Kampanya normal şekilde işlenir ve geçmişe kaydedilir, ancak
              <strong> hiç kimseye gerçek e-posta gönderilmez</strong>. Gerçek gönderim için
              sunucudaki <code>EMAIL_DRY_RUN</code> değerini <code>false</code> yapın.
            </p>
          </div>
        </div>
      )}

      {/* Progress Steps — 2 adimlik gosterge tum sayfaya yayilmasin */}
      <div className="flex items-center gap-3 max-w-lg">
        {[1, 2].map((num) => (
          <React.Fragment key={num}>
            <div className="flex items-center gap-2 shrink-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= num
                    ? 'bg-[#6366f1] text-white'
                    : 'bg-[#f8fafc] text-[#cbd5e1] border-2 border-[#e2e8f0]'
                }`}
              >
                {num}
              </div>
              <span className={`text-sm font-medium ${step >= num ? 'text-[#0f172a]' : 'text-[#cbd5e1]'}`}>
                {num === 1 ? 'Şablon Seç' : 'Alıcı Seç'}
              </span>
            </div>
            {num < 2 && <div className="flex-1 h-0.5 bg-[#e2e8f0]"></div>}
          </React.Fragment>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Step 1: Select Template */}
          {step === 1 && (
            <div className="bg-white rounded-xl border border-[#e2e8f0] p-8 space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <Mail size={24} className="text-[#6366f1]" />
                <h2 className="text-2xl font-bold text-[#0f172a]">Email Şablonu Seçin</h2>
              </div>

              {templates.length === 0 ? (
                <div className="text-center py-12">
                  <Mail size={48} className="mx-auto text-[#cbd5e1] mb-4" />
                  <p className="text-[#64748b]">Henüz şablon yok. Lütfen önce bir şablon oluşturun.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      data-testid={`template-option-${template.id}`}
                      onClick={() => setSelectedTemplate(template.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedTemplate === template.id
                          ? 'border-[#6366f1] bg-[#6366f1]/5'
                          : 'border-[#e2e8f0] hover:border-[#cbd5e1]'
                      }`}
                    >
                      <h3 className="font-bold text-[#0f172a] mb-1">{template.name}</h3>
                      <p className="text-sm text-[#64748b]">{template.subject}</p>
                    </div>
                  ))}
                </div>
              )}

              <Button
                onClick={() => setStep(2)}
                disabled={!selectedTemplate}
                data-testid="next-to-recipients-btn"
                className="w-full bg-[#6366f1] hover:bg-[#5558e3] text-white mt-6"
              >
                Sonraki: Alıcı Seç
              </Button>
            </div>
          )}

          {/* Step 2: Select Recipients */}
          {step === 2 && (
            <div className="bg-white rounded-xl border border-[#e2e8f0] p-8 space-y-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Users size={24} className="text-[#6366f1]" />
                  <h2 className="text-2xl font-bold text-[#0f172a]">Alıcı Seçin</h2>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStep(1)}
                >
                  Geri
                </Button>
              </div>

              {totalRecipients === 0 ? (
                <div className="text-center py-12">
                  <Users size={48} className="mx-auto text-[#cbd5e1] mb-4" />
                  <p className="text-[#64748b]">Henüz alıcı yok. Lütfen önce alıcı ekleyin.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Selection Mode */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-2 bg-[#f8fafc] rounded-lg">
                    <button
                      onClick={() => {
                        setSelectionMode('all');
                        selectAllRecipients();
                      }}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectionMode === 'all'
                          ? 'bg-[#6366f1] text-white'
                          : 'bg-white text-[#64748b] hover:bg-[#f1f5f9]'
                      }`}
                    >
                      Hepsine
                    </button>
                    <button
                      onClick={() => setSelectionMode('limit')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectionMode === 'limit'
                          ? 'bg-[#6366f1] text-white'
                          : 'bg-white text-[#64748b] hover:bg-[#f1f5f9]'
                      }`}
                    >
                      Sayı Limiti
                    </button>
                    <button
                      onClick={() => setSelectionMode('group')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectionMode === 'group'
                          ? 'bg-[#6366f1] text-white'
                          : 'bg-white text-[#64748b] hover:bg-[#f1f5f9]'
                      }`}
                    >
                      Grup
                    </button>
                    <button
                      onClick={() => {
                        setSelectionMode('individual');
                        setSelectedRecipients([]);
                        ensureRecipientsLoaded();
                      }}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectionMode === 'individual'
                          ? 'bg-[#6366f1] text-white'
                          : 'bg-white text-[#64748b] hover:bg-[#f1f5f9]'
                      }`}
                    >
                      Tekil
                    </button>
                    <button
                      onClick={() => {
                        setSelectionMode('segment');
                        setSelectedRecipients([]);
                        setSelectedPreset('');
                      }}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectionMode === 'segment'
                          ? 'bg-[#6366f1] text-white'
                          : 'bg-white text-[#64748b] hover:bg-[#f1f5f9]'
                      }`}
                    >
                      Segment
                    </button>
                  </div>

                  {/* Segment seçimi: etkileşim geçmişine göre dinamik hedefleme */}
                  {selectionMode === 'segment' && (
                    <div className="p-4 bg-[#f5f3ff] rounded-lg border-2 border-[#e9d5ff]">
                      <p className="font-semibold text-[#5b21b6] mb-1">Hazır Segmentler</p>
                      <p className="text-sm text-[#6b21a8] mb-4">
                        Alıcıları tek tek işaretlemek yerine davranışlarına göre hedefleyin.
                      </p>

                      <div className="grid sm:grid-cols-2 gap-2">
                        {segmentPresets.map((preset) => (
                          <button
                            key={preset.key}
                            onClick={() => applySegment(preset.key)}
                            disabled={segmentLoading}
                            className={`text-left px-4 py-3 rounded-lg border-2 transition-colors disabled:opacity-60 ${
                              selectedPreset === preset.key
                                ? 'border-[#8b5cf6] bg-white'
                                : 'border-transparent bg-white/70 hover:border-[#ddd6fe]'
                            }`}
                          >
                            <span className="font-medium text-[#0f172a]">{preset.label}</span>
                          </button>
                        ))}
                      </div>

                      {segmentLoading && (
                        <p className="mt-3 text-sm text-[#6b21a8]">Segment hesaplanıyor...</p>
                      )}

                      {!segmentLoading && selectedPreset && (
                        <div className="mt-4 p-3 bg-white rounded-lg border border-[#ddd6fe]">
                          <p className="font-bold text-[#5b21b6]">
                            ✓ {selectedRecipients.length} alıcı seçildi
                          </p>
                          <p className="text-xs text-[#6b21a8] mt-0.5">
                            {segmentPresets.find((p) => p.key === selectedPreset)?.label}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Exclude Previously Sent Checkbox */}
                  <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="exclude-sent"
                        checked={excludePreviouslySent}
                        onCheckedChange={setExcludePreviouslySent}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <label
                          htmlFor="exclude-sent"
                          className="font-semibold text-gray-800 cursor-pointer flex items-center gap-2"
                        >
                          🚫 Daha Önce Bu Kampanyayı Alanlara Gönderme
                        </label>
                        <p className="text-sm text-gray-600 mt-1">
                          Bu seçeneği işaretlerseniz, bu şablonla daha önce email gönderilen kişiler listeden çıkarılır.
                          {previouslySentCount > 0 && (
                            <span className="text-orange-600 font-semibold ml-1">
                              ({previouslySentCount} kişi hariç tutulacak)
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Limit Selection */}
                  {selectionMode === 'limit' && (
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200">
                      <Label className="font-medium text-[#0f172a] mb-3 block text-lg">
                        📊 Kaç Kişiye Gönderilsin?
                      </Label>
                      <div className="flex gap-2 mb-3">
                        <input
                          type="number"
                          placeholder="Örn: 100, 300, 500"
                          value={recipientLimit}
                          onChange={(e) => setRecipientLimit(e.target.value)}
                          className="flex-1 px-4 py-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:outline-none text-lg font-semibold"
                          min="1"
                          max={totalRecipients}
                        />
                        <Button
                          onClick={handleLimitSelection}
                          className="bg-blue-600 hover:bg-blue-700 px-6"
                        >
                          Seç
                        </Button>
                      </div>
                      <p className="text-sm text-gray-600">
                        💡 Toplam <strong>{totalRecipients.toLocaleString('tr-TR')}</strong> alıcı mevcut.
                        {recipientLimit && ` ${Math.min(parseInt(recipientLimit) || 0, totalRecipients)} kişi rastgele seçilecek.`}
                      </p>
                    </div>
                  )}

                  {/* Group Selection */}
                  {selectionMode === 'group' && (
                    <div className="p-4 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]">
                      <Label className="font-medium text-[#0f172a] mb-2 block">Grup Seçin:</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {uniqueGroups.map((group) => (
                          <button
                            key={group.name}
                            onClick={() => handleGroupSelection(group.name)}
                            disabled={selectionLoading}
                            className={`px-4 py-3 rounded-lg font-medium text-left transition-colors disabled:opacity-60 ${
                              selectedGroup === group.name
                                ? 'bg-[#6366f1] text-white'
                                : 'bg-white text-[#64748b] hover:bg-[#f1f5f9] border border-[#e2e8f0]'
                            }`}
                          >
                            <div className="font-bold">{group.name}</div>
                            <div className="text-xs opacity-80">
                              {group.total.toLocaleString('tr-TR')} alıcı
                            </div>
                          </button>
                        ))}
                      </div>
                      {selectedGroup && (
                        <p className="text-sm text-[#10b981] mt-3 font-medium">
                          ✓ {selectedRecipients.length} alıcı seçildi ({selectedGroup})
                        </p>
                      )}
                    </div>
                  )}

                  {/* Individual Selection */}
                  {selectionMode === 'individual' && recipientsLoading && (
                    <div className="flex items-center justify-center gap-3 py-12 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#6366f1]"></div>
                      <p className="text-sm text-[#64748b]">
                        {totalRecipients.toLocaleString('tr-TR')} alıcı yükleniyor…
                      </p>
                    </div>
                  )}

                  {selectionMode === 'individual' && !recipientsLoading && (
                    <>
                      <div className="flex items-center gap-2 p-4 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]">
                        <Checkbox
                          checked={selectedRecipients.length === filteredRecipients.length && filteredRecipients.length > 0}
                          onCheckedChange={handleSelectAll}
                          data-testid="select-all-recipients"
                        />
                        <label className="text-sm font-medium text-[#0f172a]">
                          Tümünü Seç ({filteredRecipients.length.toLocaleString('tr-TR')} alıcı)
                        </label>
                      </div>

                      <input
                        type="text"
                        value={recipientSearch}
                        onChange={(e) => setRecipientSearch(e.target.value)}
                        placeholder="Email veya isim ara…"
                        data-testid="individual-recipient-search"
                        className="w-full px-3 py-2 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
                      />

                      {/* Liste uzunsa sadece ilk N satır çizilir — binlerce
                          checkbox tarayıcıyı kilitliyor. Seçim bundan etkilenmez. */}
                      {searchedRecipients.length > INDIVIDUAL_LIST_CAP && (
                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          {searchedRecipients.length} alıcıdan ilk {INDIVIDUAL_LIST_CAP} tanesi
                          gösteriliyor. Aramayla daraltın ya da "Tümünü Seç" kullanın.
                        </p>
                      )}

                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {visibleRecipients.map((recipient) => (
                      <div
                        key={recipient.id}
                        data-testid={`recipient-checkbox-${recipient.id}`}
                        className="flex items-center gap-3 p-3 hover:bg-[#f8fafc] rounded-lg border border-[#e2e8f0] transition-colors"
                      >
                        <Checkbox
                          checked={selectedRecipients.includes(recipient.id)}
                          onCheckedChange={() => handleRecipientToggle(recipient.id)}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-[#0f172a]">{recipient.email}</p>
                          <p className="text-sm text-[#64748b]">{recipient.name || 'No name'}</p>
                        </div>
                        {recipient.group && (
                          <span className="px-2 py-1 bg-[#6366f1]/10 text-[#6366f1] rounded-md text-xs font-medium">
                            {recipient.group}
                          </span>
                        )}
                        </div>
                      ))}
                    </div>
                  </>
                  )}

                  {/* All mode info */}
                  {selectionMode === 'all' && (
                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                      <p className="text-green-800 font-medium">
                        ✓ Tüm alıcılara gönderilecek ({(selectedRecipients.length || totalRecipients).toLocaleString('tr-TR')} alıcı)
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar - Summary */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-[#e2e8f0] p-5 sticky top-8">
            <h3 className="text-lg font-bold text-[#0f172a] mb-6">Kampanya Özeti</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-[#64748b] mb-2">Şablon</p>
                <p className="text-[#0f172a] font-medium">
                  {selectedTemplate
                    ? templates.find(t => t.id === selectedTemplate)?.name
                    : 'Seçilmedi'}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-[#64748b] mb-2">Alıcılar</p>
                <p className="text-[#0f172a] font-medium">
                  {selectedRecipients.length} seçildi
                </p>
              </div>

              {step === 2 && (
                <div className="space-y-4 pt-4 border-t border-[#e2e8f0]">
                  <div>
                    <Label htmlFor="schedule-date" className="text-sm font-medium text-[#64748b] mb-2">Zamanlama (İsteğe Bağlı)</Label>
                    <input
                      id="schedule-date"
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      className="w-full px-3 py-2 border border-[#e2e8f0] rounded-lg mt-1"
                    />
                  </div>
                  {scheduleDate && (
                    <div>
                      <input
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        className="w-full px-3 py-2 border border-[#e2e8f0] rounded-lg"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Yakın zamanda mail almış alıcı uyarısı */}
            {step === 2 && recentCheck && recentCheck.recent_count > 0 && (
              <div
                data-testid="recent-send-warning"
                className="mt-6 rounded-xl border-2 border-amber-400 bg-amber-50 p-4"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle size={22} className="text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-amber-900">Dikkat: Yakın zamanda mail gönderildi</p>
                    <p className="text-sm text-amber-800 mt-1">
                      Seçtiğiniz {recentCheck.total_selected} kişiden{' '}
                      <span className="font-bold">{recentCheck.recent_count}</span> tanesi son{' '}
                      {recentCheck.within_days} gün içinde zaten mail aldı. Gönderirseniz onay
                      istenecek.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <Button
                onClick={handleSend}
                disabled={loading || checkingRecent || !selectedTemplate || selectedRecipients.length === 0}
                data-testid="send-campaign-btn"
                className="w-full bg-[#6366f1] hover:bg-[#5558e3] text-white mt-6 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Kampanya Gönder
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Yakın zamanda mail almış alıcılar için "EVET" yazdıran onay penceresi */}
      <Dialog open={confirmOpen} onOpenChange={(open) => { if (!open) setConfirmText(''); setConfirmOpen(open); }}>
        <DialogContent className="max-w-xl" data-testid="recent-send-confirm-dialog">
          <DialogHeader>
            <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-2">
              <AlertTriangle size={34} className="text-amber-600" />
            </div>
            <DialogTitle className="text-2xl font-bold text-center text-amber-900">
              Bu kişilere yakın zamanda mail gönderildi!
            </DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              Seçtiğiniz{' '}
              <span className="font-bold text-[#0f172a]">{recentCheck?.total_selected}</span> alıcıdan{' '}
              <span className="font-bold text-amber-700">{recentCheck?.recent_count}</span> tanesi son{' '}
              <span className="font-bold text-[#0f172a]">{recentCheck?.within_days} gün</span> içinde
              zaten mail aldı. Tekrar göndermek spam şikâyetine ve abonelikten çıkışlara yol açabilir.
            </DialogDescription>
          </DialogHeader>

          {recentCheck?.samples?.length > 0 && (
            <div className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] max-h-48 overflow-y-auto">
              {recentCheck.samples.map((s) => (
                <div
                  key={s.email}
                  className="flex items-center justify-between gap-3 px-4 py-2 border-b border-[#e2e8f0] last:border-b-0 text-sm"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-[#0f172a] truncate">{s.email}</p>
                    {s.template_name && (
                      <p className="text-xs text-[#64748b] truncate">{s.template_name}</p>
                    )}
                  </div>
                  <span className="text-xs text-amber-700 font-medium whitespace-nowrap">
                    {s.days_ago === 0 ? 'bugün' : `${s.days_ago} gün önce`}
                  </span>
                </div>
              ))}
              {recentCheck.recent_count > recentCheck.samples.length && (
                <div className="px-4 py-2 text-xs text-[#64748b]">
                  ve {recentCheck.recent_count - recentCheck.samples.length} kişi daha…
                </div>
              )}
            </div>
          )}

          <div className="pt-2">
            <Label htmlFor="confirm-word" className="text-sm font-medium text-[#0f172a]">
              Yine de göndermek için aşağıya <span className="font-bold">EVET</span> yazın
            </Label>
            <input
              id="confirm-word"
              type="text"
              autoComplete="off"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmAndSend(); }}
              placeholder="EVET"
              data-testid="recent-send-confirm-input"
              className="w-full px-3 py-2 border-2 border-[#e2e8f0] rounded-lg mt-2 focus:border-amber-400 focus:outline-none"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setConfirmOpen(false); setConfirmText(''); }}
              data-testid="recent-send-cancel-btn"
            >
              Vazgeç
            </Button>
            <Button
              onClick={confirmAndSend}
              disabled={confirmText.trim().toLocaleUpperCase('tr-TR') !== CONFIRM_WORD || loading}
              data-testid="recent-send-confirm-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-2"
            >
              <Send size={18} />
              Yine de Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SendCampaign;